import { cloneDeep, isEmpty } from 'lodash-es';
import React, { useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import {
  getTargetingPresetHeaderBidderList,
  ISimpleTargetingPresetHeaderBidder,
} from '~/src/dash/apiV2/targeting/targetingPresets';
import { SearchResultsText } from '~/src/dash/components/input/common';
import { highlightedDivStyle } from '~/src/dash/components/layout/common';
import { IDNameTableModal } from '~/src/dash/components/modals/IDNameTableModal';
import { ListViewTable } from '~/src/dash/components/tables/ListViewTable';
import {
  getComparator,
  IListViewTableColumnInfo,
  IListViewTableRow,
  Order,
  RowsPerPage,
  stableSort,
} from '~/src/dash/components/tables/ListViewTable/common';
import { SimpleTabs } from '~/src/dash/components/tabs/SimpleTabs';
import {
  Col,
  Cols,
  Panel,
  Panels,
  Row,
  Rows,
  spacing,
  Text,
  TextInput,
} from '~/src/dash/publicaUI';
import {
  extractRouteParams,
  getRoutePath,
  RouteBidderSettingsExisting,
} from '~/src/dash/router/routes';
import { publicaColors } from '~/src/dash/utils/colors';
import {
  LoadingState,
  LoadingStateHasData,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import { useBaseConfig, useBaseStore } from '~/src/dash/views/base/contexts';
import { SnackbarView } from '~/src/dash/views/snackbar-view';
import {
  formatTargetingPresetResponseIntoLVTRows,
  TargetingPresetTypeKey,
} from '~/src/dash/views/tools/TargetingPresets/config';
import { ITargetingPresetListViewConfig } from '~/src/dash/views/tools/TargetingPresets/config';
import { CreateNewTargetingPresetButton } from '~/src/dash/views/tools/TargetingPresets/CreateNewTargetingPresetButton';
import { TopBar } from '~/src/dash/views/topbar/TopBar';

const queryData = (data: ISimpleTargetingPresetHeaderBidder[], query: string) => {
  let queriedRows = cloneDeep(data);
  if (query !== '') {
    queriedRows = data.filter((row) => {
      for (const key of ['ID', 'Name']) {
        // @ts-ignore
        if (row[key].toString().toLowerCase().includes(query.toLowerCase())) {
          return true;
        }
      }
      return false;
    });
  }

  return queriedRows;
};

export const TargetingPresets = () => {
  const isMounted = useRef(false);
  const { session, user } = useBaseStore();
  const { Tools } = useBaseConfig();
  const cfg = Tools.Targeting.Main as ITargetingPresetListViewConfig;
  const { defaultState, presetTypes } = cfg;

  // load the preset type
  const [activePreset, setActivePreset] = useState(presetTypes[defaultState.activePresetType]);
  cfg.isInitialized = true;

  const [activeTabIndex, setActiveTabIndex] = useState(0);

  // states and refs
  const [responseData, setResponseData] = useState<ISimpleTargetingPresetHeaderBidder[]>([]);
  const [queriedData, setQueriedData] = useState<ISimpleTargetingPresetHeaderBidder[]>([]);
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [loadState, setLoadState] = useState<LoadingState>(LoadingStateLoading);
  const [isTableActionPending, setIsTableActionPending] = useState(false);
  const [order, setOrder] = useState<Order>(activePreset.defaultState.order || 'asc');
  const [orderBy, setOrderBy] = useState<string>(activePreset.defaultState.orderBy || '');
  const [page, setPage] = useState(activePreset.defaultState.page || 0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultState.rowsPerPage || 10);
  const [sortedRows, setSortedRows] = useState([] as IListViewTableRow[]);

  const [query, setQuery] = useState('');
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarError, setSnackbarError] = useState(false);

  // bidders modal
  // associated bidders dialog
  const [openAssociatedBidders, setOpenAssociatedBidders] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState({} as ISimpleTargetingPresetHeaderBidder);

  useEffect(() => {
    getData(activePreset.key);
  }, []);

  useEffect(() => {
    if (isMounted.current) {
      const queriedRows = queryData(responseData, query);
      unstable_batchedUpdates(() => {
        setQueriedData(queriedRows);
        setTotalRowCount(queriedRows.length);
        setPage(0);
      });
    }
  }, [responseData, query]);

  useEffect(() => {
    if (isMounted.current) {
      if (!isEmpty(queriedData)) {
        setSortedRows(
          stableSort(
            rows,
            getComparator(order, orderBy, activePreset.table.columns[orderBy].valueType),
          ),
        );
      } else {
        setSortedRows([]);
      }
    }
  }, [order, orderBy, queriedData]);

  useEffect(() => {
    if (isMounted.current) {
      unstable_batchedUpdates(() => {
        setLoadState(LoadingStateLoading);
        setQuery('');
        setOrder(activePreset.defaultState.order);
        setOrderBy(activePreset.defaultState.orderBy);
        setPage(0);
        getData(activePreset.key);
      });
      cfg.defaultState.activePresetType = activePreset.key;
    }
  }, [activePreset.key]);

  useEffect(() => {
    if (isMounted.current) {
      activePreset.defaultState.page = page;
      activePreset.defaultState.order = order;
      activePreset.defaultState.orderBy = orderBy;
    } else {
      isMounted.current = true;
    }
  }, [page, order, orderBy]);

  const getData = (activePresetType: TargetingPresetTypeKey) => {
    setLoadState(LoadingStateLoading);
    switch (activePresetType) {
      case 'bidder':
        getTargetingPresetHeaderBidderList(session.publisher.ID, {})
          .then((res) => {
            if (res && res.Presets) {
              setResponseData(res.Presets || []);
              setTotalRowCount(res.NumPresets);
            }
            setLoadState(LoadingStateHasData);
          })
          .catch((err) => {
            console.error(err);
            unstable_batchedUpdates(() => {
              setSnackbarMessage('Error while loading presets. Please wait and try again');
              setSnackbarError(true);
              setResponseData([]);
              setTotalRowCount(0);
              setLoadState(LoadingStateNoData);
            });
          });
        return;
      case 'campaign':
        // TODO: CORRECT THIS WHEN READY
        unstable_batchedUpdates(() => {
          setSnackbarMessage('Campaign Targeting Presets are currently disabled.');
          setSnackbarError(true);
          setResponseData([]);
          setTotalRowCount(0);
          setLoadState(LoadingStateNoData);
        });
        return;
      default:
        unstable_batchedUpdates(() => {
          setSnackbarMessage('Error while loading page. Please refresh and try again.');
          setSnackbarError(true);
          setResponseData([]);
          setTotalRowCount(0);
          setLoadState(LoadingStateNoData);
        });
        return;
    }
  };

  const handleChangeTab = (_event: React.SyntheticEvent, index: number) => {
    const tabKey = cfg.presetTabs.find((tab) => tab.value === index)?.key;
    unstable_batchedUpdates(() => {
      setActivePreset(cfg.presetTypes[tabKey as TargetingPresetTypeKey]);
      setActiveTabIndex(index);
    });
  };

  const handleOnClickColumnHeader = (column: IListViewTableColumnInfo) => {
    const { orderByKey, requestKey, defaultOrderDirection } = column;
    const key = orderByKey || requestKey;

    if (orderBy !== key) {
      unstable_batchedUpdates(() => {
        setOrder(defaultOrderDirection || 'asc');
        setOrderBy(key);
        setPage(0);
      });
    } else {
      const isAsc = orderBy === key && order === 'asc';
      unstable_batchedUpdates(() => {
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(key);
        setPage(0);
      });
    }
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = Number(event.target.value) as RowsPerPage;
    unstable_batchedUpdates(() => {
      setRowsPerPage(newRowsPerPage);
      setPage(0);
    });
    defaultState.rowsPerPage = newRowsPerPage;
  };

  const handleChangePage = (_e: unknown, newPage: number) => {
    setPage(newPage);
    activePreset.defaultState.page = newPage;
  };

  const handleOnChangeQuery = (text: string) => {
    setQuery(text);
  };

  const handleDeleteItem = (id: string) => {
    setIsTableActionPending(true);
    activePreset
      .deletePresetFn(session.publisher.ID, id)
      .then(() => {
        unstable_batchedUpdates(() => {
          setIsTableActionPending(false);
          setSnackbarMessage('Successfully deleted preset.');
          setSnackbarError(false);
        });
        getData(activePreset.key);
      })
      .catch((err) => {
        console.error(err);
        unstable_batchedUpdates(() => {
          setIsTableActionPending(false);
          setSnackbarMessage('Error while deleting preset.');
          setSnackbarError(true);
        });
      });
    return new Promise((resolve) => resolve(undefined));
  };

  const handleClickOnAssociatedBidders = (id: string) => {
    const newSelectedPreset = responseData.find(({ ID }) => ID === Number(id));
    if (!isEmpty(newSelectedPreset)) {
      unstable_batchedUpdates(() => {
        setSelectedPreset(newSelectedPreset || {});
        setOpenAssociatedBidders(true);
      });
    } else {
      return;
    }
  };

  const handleCloseAssociatedBiddersDialog = () => {
    unstable_batchedUpdates(() => {
      setOpenAssociatedBidders(false);
      setSelectedPreset({} as ISimpleTargetingPresetHeaderBidder);
    });
  };

  const handleSnackbarClose = () => {
    setSnackbarMessage('');
  };

  const getBidderSettingsRoute = (id: string): string => {
    return getRoutePath(RouteBidderSettingsExisting, extractRouteParams({ session, user }), {
      b: id,
    });
  };

  const rows = formatTargetingPresetResponseIntoLVTRows(queriedData, activePreset.table);
  const visibleRows = sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Panels max vertical relative minWidthMain>
      <Panel largeHorizontalPadding smallVerticalPadding>
        <TopBar title='Targeting Presets' enableUINotices />
      </Panel>
      <Panel
        largeHorizontalPadding
        smallVerticalPadding
        style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}
      >
        <Cols gap={spacing.base} style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <Col flex>
            <Text extraLarge bold>
              Presets
            </Text>
          </Col>
          <Col>
            <Cols gap={spacing.small} style={{ alignItems: 'center' }}>
              <Col>
                <CreateNewTargetingPresetButton />
              </Col>
            </Cols>
          </Col>
        </Cols>
      </Panel>
      <Panel>
        <SimpleTabs
          activeTabIndex={activeTabIndex}
          ariaLabel='Presets'
          handleChangeTab={handleChangeTab}
          tabs={cfg.presetTabs}
        />
      </Panel>
      <Panel largeHorizontalPadding smallVerticalPadding marginTop={spacing.base}>
        <Rows gap={spacing.base}>
          <Row largeHorizontalPadding smallVerticalPadding style={highlightedDivStyle}>
            <Rows>
              <Row style={{ padding: '8px 0px' }}>
                <Cols style={{ justifyContent: 'space-between' }}>
                  <Col>
                    <Cols
                      wrap
                      gap={spacing.medium}
                      style={{
                        alignItems: 'center',
                        rowGap: spacing.small,
                      }}
                    >
                      <Col>
                        <Cols
                          wrap
                          gap={spacing.medium}
                          style={{
                            alignItems: 'center',
                            rowGap: spacing.small,
                          }}
                        >
                          <Col>
                            <TextInput
                              enableClear
                              handleOnChange={handleOnChangeQuery}
                              msDelay={500}
                              placeholder='Search by Name or ID'
                              prefix='search'
                            />
                          </Col>
                          <Col>
                            <SearchResultsText
                              numRowsDisplayed={responseData.length}
                              numTotalRows={totalRowCount}
                            />
                          </Col>
                        </Cols>
                      </Col>
                    </Cols>
                  </Col>
                </Cols>
              </Row>
              <Row noPadding marginTop={spacing.large}>
                <ListViewTable
                  className={cfg.cypressClassName}
                  loadState={loadState}
                  activeColumnKeys={activePreset.defaultState.activeColumnKeys}
                  columns={activePreset.table.columns}
                  customFunctions={{ handleClickOnAssociatedBidders }}
                  order={order}
                  orderBy={orderBy}
                  page={page}
                  rows={visibleRows}
                  rowsPerPage={rowsPerPage}
                  totalRowCount={totalRowCount}
                  handleOnClickColumnHeader={handleOnClickColumnHeader}
                  handleChangePage={handleChangePage}
                  handleChangeRowsPerPage={handleChangeRowsPerPage}
                  isActionPending={isTableActionPending}
                  isExpanded={false}
                  isReadAuthorized={true}
                  isWriteAuthorized={true}
                  enableActions
                  handleDeleteItem={handleDeleteItem}
                  handleCloneItem={activePreset.getHandleClone(session, user)}
                  getItemSettingsRoute={activePreset.getSettingsRouteFn(session, user)}
                  customDeleteMessage={activePreset.customDeleteMessage}
                  key={activePreset.label}
                />
              </Row>
            </Rows>
          </Row>
        </Rows>
      </Panel>
      {!isEmpty(selectedPreset?.Bidders) && (
        <IDNameTableModal
          open={openAssociatedBidders}
          onClose={handleCloseAssociatedBiddersDialog}
          title='Associated Bidders'
          subTitle={selectedPreset.Name}
          customFunctions={{ handleCloseAssociatedBiddersDialog }}
          data={selectedPreset?.Bidders || []}
          getItemSettingsRoute={getBidderSettingsRoute}
        />
      )}
      <SnackbarView open={!!snackbarMessage} error={!!snackbarError} onClose={handleSnackbarClose}>
        {snackbarMessage}
      </SnackbarView>
    </Panels>
  );
};
